import React, { useEffect } from "react";
import {
  Banner,
  Button,
  EmptyState,
  FileIcon,
  Flex,
  LoadingIndicator,
  Text,
} from "@jsq/ds/core";
import { useDocumentViewerUrl } from "#hooks/useDocumentViewerUrl";
import { PdfPanel } from "../DocumentViewer/components/PdfPanel";
import {
  PdfNavigationProvider,
  usePdfNavigation,
} from "../DocumentViewer/context/PdfNavigationContext";
import * as styles from "./styles_vanilla.css";

export type PdfViewerProps = {
  documentId: string;
  documentVersion?: number;
  /** Cited page to land on (1-based), e.g. a finding's provenance page. The
   *  server clamps it to the document's range; omit to default to page 1. */
  initialPage?: number;
  /** Shown in the header until the resolved viewer URL supplies its own filename. */
  filename?: string;
  /** Document-type label shown under the filename in the document-preview header. */
  documentType?: string;
  /** When set, the header shows a labeled back control (finding-provenance flow)
   *  instead of the document-preview name/type header with a close icon. */
  backLabel?: string;
  onBack: () => void;
  /** Arena the document belongs to; undefined until the host app resolves it,
   *  which skips the viewer-URL read. */
  arenaId: number | undefined;
};

/** Filename extension (lowercase, no dot), or `null` when the filename has none. */
const extensionOf = (name: string | undefined): string | null => {
  if (!name) {
    return null;
  }
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) {
    return null;
  }
  return name.slice(dot + 1).toLowerCase();
};

const PdfViewerContent: React.FC<PdfViewerProps> = ({
  documentId,
  documentVersion,
  initialPage: requestedPage,
  filename,
  documentType,
  backLabel,
  onBack,
  arenaId,
}) => {
  // Non-PDF documents (e.g. `.xlsb` workbooks) can't render inside the PDF
  // iframe, and fay's viewer-URL endpoint errors on them — which would
  // otherwise surface a misleading "Couldn't load this document" retry banner.
  // Short-circuit from the filename extension BEFORE firing the query so the
  // user sees a plain "Preview not available" state and no wasted round trip.
  const extension = extensionOf(filename);
  const isPdfByExtension = extension === null || extension === "pdf";

  const { viewer, loading, error, refresh } = useDocumentViewerUrl(
    isPdfByExtension ? documentId : null,
    arenaId,
    { documentVersion },
  );
  const { navigateToPage } = usePdfNavigation();

  // Drive `PdfPanel` to the requested page. PdfPanel keys the iframe by
  // targetPage, so setting it here remounts the iframe with the correct
  // `#page=N` URL — reliable across PDF viewers where fragment-only edits are
  // not. Coalesce `undefined` (plain doc open, no section requested) to page 1
  // so switching from a doc opened at a section-page to a plain doc open
  // remounts on page 1 — without this, `targetPage` stays at the previous
  // section's page and the new doc opens there instead of at the top.
  useEffect(() => {
    navigateToPage(requestedPage ?? 1);
  }, [documentId, requestedPage, navigateToPage]);

  // `||` (not `??`): the resolved viewer filename can come back as an empty
  // string, which should fall back to the passed filename rather than blank out
  // — for both the header and the PdfPanel/iframe title.
  const title = viewer?.filename || filename || "Document";
  // Header icon reflects the real document type (workbooks, etc.), not always pdf.
  const headerExtension = extension ?? "pdf";

  const renderBody = (): JSX.Element => {
    // Non-PDF: query was skipped above; show a plain "not available" state so
    // the user isn't left staring at a retry banner for a file that will never
    // preview inline.
    if (!isPdfByExtension) {
      return (
        <EmptyState
          illustration="searching"
          title="Preview not available"
          description="This document type can’t be previewed in the viewer."
        />
      );
    }

    if (loading && !viewer) {
      return (
        <Flex alignItems="center" gap={8}>
          <LoadingIndicator size="small" />
          <Text variant="secondary-md" asSpan>
            Loading document…
          </Text>
        </Flex>
      );
    }

    // A load failure (transient network/Apollo error) is retryable; surface it
    // with a retry rather than the terminal "unavailable" empty state below.
    if (error) {
      return (
        <Flex flexDirection="column" gap={8}>
          <Banner
            variant="danger"
            title="Couldn’t load this document"
            description="Please try again."
          />
          <Button variant="neutral" icon="repeat" onClick={refresh}>
            Try again
          </Button>
        </Flex>
      );
    }

    if (!viewer) {
      return (
        <EmptyState
          illustration="searching"
          title="Document unavailable"
          description="This document can’t be viewed right now."
        />
      );
    }

    // Only PDFs render inline via the iframe (`PdfPanel` is PDF-only). For other
    // document types the server returns their real `contentType` (e.g. .xlsb
    // workbooks come back as an Excel MIME) — surface a plain "not available"
    // message rather than trying to embed a non-PDF in the PDF viewer.
    // `startsWith` covers charset-suffixed variants (`application/pdf; charset=…`).
    if (!viewer.contentType.startsWith("application/pdf")) {
      return (
        <EmptyState
          illustration="searching"
          title="Preview not available"
          description="This document type can’t be previewed in the viewer."
        />
      );
    }

    // Pass the raw presigned URL (no fragment) to PdfPanel. PdfPanel appends
    // `#page=N` via `buildPdfUrlWithPage(url, targetPage)` and keys the iframe
    // on targetPage — so the section jump is a full iframe remount with a
    // clean, canonical URL. Adding our own fragment here would chain into
    // PdfPanel's `#page=N` (producing `#view=FitH#page=N`) and break the
    // parser, which is exactly the bug that had every section click falling
    // back to page 1.
    return (
      <div className={styles.pdfHost}>
        <PdfPanel url={viewer.pdfUrl} title={title} onRefreshUrl={refresh} />
      </div>
    );
  };

  return (
    <Flex flexDirection="column" className={styles.wrapper}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={8}
        className={styles.header}
      >
        {backLabel ? (
          // Finding-provenance flow: a labeled control back to the finding.
          <Flex alignItems="center" gap={8}>
            <Button variant="neutral" icon="arrow-left" onClick={onBack}>
              {backLabel}
            </Button>
            <Text variant="strong-md" asSpan lines={1}>
              {title}
            </Text>
          </Flex>
        ) : (
          // Document-preview flow: filename + type on the left, close on the right.
          <>
            <Flex alignItems="center" gap={8}>
              <FileIcon extension={headerExtension} size="small" />
              <Flex flexDirection="column">
                <Text variant="strong-sm" asSpan lines={1}>
                  {title}
                </Text>
                {documentType ? (
                  <Text variant="secondary-sm" asSpan lines={1}>
                    {documentType}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
            <Button
              variant="neutral"
              icon="times"
              aria-label="Close document preview"
              onClick={onBack}
            />
          </>
        )}
      </Flex>
      {renderBody()}
    </Flex>
  );
};

/**
 * PdfViewer — thin wrapper over `PdfPanel`. Fetches a short-lived presigned URL
 * via `documentViewerUrl`, renders it in an iframe, and refreshes the URL ahead
 * of its ~300s TTL. Renders inside `PdfNavigationProvider` so finding→page
 * scroll (`navigateToPage`) drives the iframe; lands on the server's
 * `initialPage` on first resolve, keyed on documentId + initialPage so a
 * mid-session URL refresh doesn't yank the reader back to it. The full DDG
 * `DocumentViewer` is intentionally not reused — this is read-only and carries
 * no verification panel.
 */
export const PdfViewer: React.FC<PdfViewerProps> = (props) => (
  <PdfNavigationProvider>
    <PdfViewerContent {...props} />
  </PdfNavigationProvider>
);
